import React from 'react'
import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { EvidenceShowTabBody } from 'src/pages/Evidence/evidence.styles'
import { useInvalidateEvidence } from 'src/Utils/swrCacheInvalidation/useInvalidateEvidence'
import { useEvidenceAudits } from 'src/pages/Evidence/evidence.hooks'
import { Spinner } from 'src/Throbber/Spinner'
import { useModalState } from 'src/Modal/ModalStateContext'
import log from 'loglevel'
import { FlexRow } from 'src/components/Reusable/Flex'
import { EyeIcon } from 'src/components/Icons/Basic/EyeIcon'
import pluralize from 'pluralize'
import { ModalFormId } from 'src/components/ModalForms'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { PERMISSIONS } from 'src/config/roleConfig'
import { ShowEvidenceAuditsModal } from 'src/pages/Evidence/modals/ShowEvidenceAuditsModal'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { Gravatar } from 'src/components/Gravatar'
import {
  Tooltip,
  TooltipOverlayType,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { InfoButton } from 'src/components/PageLayout/InfoButton'
import { EvidenceRelevantDateButton } from 'src/pages/Evidence/evidence.components'
import { formatISODate, formatISODateTime } from 'src/Utils/formatDate'
import { ViewInSourceButton } from 'src/components/Reusable/Evidence/evidence.components'
import { WORKFLOW_RECEPTOR_MODEL_IDS } from 'src/pages/Receptors/receptors.constants'
import {
  PropertiesGrid,
  PropertiesLabel,
  PropertiesRow,
  PropertiesValue,
} from './Properties.styles'

export const EvidenceAuditRowItem = ({
  audit,
}: {
  audit: AuditRecord
}): JSX.Element => {
  const auditName = audit.getName()
  const startDate = audit.getStartDate()
  const endDate = audit.getEndDate()
  return (
    <li>
      {auditName}. {startDate ? formatISODate(startDate) : ''}
      {endDate ? `-${formatISODate(endDate)}` : ''}
    </li>
  )
}

export const EvidenceAuditsRow = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const { data, isLoading, error } = useEvidenceAudits({
    caption: evidence.getCaption(),
    contentId: evidence.getContentId(),
    discoveryId: evidence.getDiscoveryId(),
    relevantDate: evidence.getRelevantDate() || new Timestamp(),
  })
  const { openModal } = useModalState()
  if (isLoading) {
    return <Spinner size="l" color="primary" />
  } else if (!data) {
    if (error) {
      log.error('Error loading evidence audits', error)
    }
    return <></>
  }

  const audits = data.getItemsList()

  return (
    <>
      <PropertiesRow>
        <PropertiesLabel>Audits</PropertiesLabel>
        <PropertiesValue>
          <FlexRow gap={5}>
            <EyeIcon />
            <TextButton
              requiredPermissions={[PERMISSIONS.READ]}
              onClick={() => openModal(ModalFormId.EVIDENCE_AUDITS)}
            >
              {pluralize('audit', audits.length, true)}
            </TextButton>
          </FlexRow>
        </PropertiesValue>
      </PropertiesRow>
      <ShowEvidenceAuditsModal audits={audits} />
    </>
  )
}

export const EvidenceAddedByRow = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const actorEmail = evidence.getActorEmail()

  return (
    <PropertiesRow>
      <PropertiesLabel>Added By</PropertiesLabel>
      <PropertiesValue>
        <Gravatar email={actorEmail} size={20} emptyIsUnassigned={false} />
      </PropertiesValue>
    </PropertiesRow>
  )
}

export const EvidenceRelevantDateRow = ({
  evidence,
  onUpdateRelevantDate,
}: {
  evidence: EvidenceGroup
  onUpdateRelevantDate?: () => void
}): JSX.Element => {
  const relevantDate = evidence.getRelevantDate()

  if (!relevantDate) return <></>

  return (
    <PropertiesRow>
      <PropertiesLabel>
        <FlexRow gap={8}>
          Relevant Date
          <Tooltip
            id="relevant-date-tooltip"
            overlayType={TooltipOverlayType.popover}
            tooltipBody="Relevant date that the evidence addresses. If your evidence covers a date range, use the end date."
          >
            <InfoButton />
          </Tooltip>
        </FlexRow>
      </PropertiesLabel>
      <PropertiesValue>
        <EvidenceRelevantDateButton
          evidenceInstanceId={evidence.getEvidenceInstanceId()}
          relevantDate={relevantDate}
          onUpdateRelevantDate={onUpdateRelevantDate}
        />
      </PropertiesValue>
    </PropertiesRow>
  )
}

export const EvidencePropertiesBody = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const isManual = !evidence.getIsAutomated()
  const createdAt = evidence.getCreatedAt() || new Timestamp()
  const createdAtVerb = isManual ? 'Uploaded' : 'Created'
  const isLinkedDocument = evidence.getIsLinkedDocument()
  const lastScannedAt = evidence.getDocScannedAt()
  const lastModifiedAt = evidence.getDocModifiedAt()
  const importLink = evidence.getDocImportLink()
  const receptorSource = evidence.getActorEmail()

  // TODO: Convert this to receptor type checking
  const isWorkflowAttachment = WORKFLOW_RECEPTOR_MODEL_IDS.includes(
    receptorSource,
  )
    ? true
    : false

  const mutate = useInvalidateEvidence()

  return (
    <EvidenceShowTabBody>
      <PropertiesGrid>
        <EvidenceAddedByRow evidence={evidence} />
        <EvidenceAuditsRow evidence={evidence} />
        {(isManual || isLinkedDocument) && (
          <EvidenceRelevantDateRow
            evidence={evidence}
            onUpdateRelevantDate={mutate}
          />
        )}
        {!isLinkedDocument && (
          <PropertiesRow>
            <PropertiesLabel>{`${createdAtVerb} Date`}</PropertiesLabel>
            <PropertiesValue>{formatISODateTime(createdAt)}</PropertiesValue>
          </PropertiesRow>
        )}
        {(isLinkedDocument || isWorkflowAttachment) && importLink && (
          <PropertiesRow>
            <PropertiesLabel>Linked Document</PropertiesLabel>
            <PropertiesValue>
              <ViewInSourceButton
                receptorModelId={evidence.getActorEmail()}
                link={importLink}
              />
            </PropertiesValue>
          </PropertiesRow>
        )}
        {isLinkedDocument && lastModifiedAt && (
          <PropertiesRow>
            <PropertiesLabel>Last Modified</PropertiesLabel>
            <PropertiesValue>
              {formatISODateTime(lastModifiedAt)}
            </PropertiesValue>
          </PropertiesRow>
        )}
        {isLinkedDocument && lastScannedAt && (
          <PropertiesRow>
            <PropertiesLabel>Last Checked</PropertiesLabel>
            <PropertiesValue>
              {formatISODateTime(lastScannedAt)}
            </PropertiesValue>
          </PropertiesRow>
        )}
      </PropertiesGrid>
    </EvidenceShowTabBody>
  )
}
